import { useState, useMemo, useRef, useEffect } from "react";

import {
	removeOnlySepNotDecimalPoint,
	addThousandSeparator,
	correctDecimals,
} from "../utils/NumberHelper";
import { getCcyDataByCcy } from "../utils/CurrencyHelper";

const MAX_NUM_CHAR_LENGTH = 15;

export const useNumberInput = (
	ccy: string,
	onUpdate: (value: string) => void,
	value: string,
	onPlaceHolderChange?: (value: string) => void
) => {
	const [isFocused, setFocused] = useState(false);
	const prevVal = useRef("");

	const {
		inputRegEx,
		numDecimals,
		placeholder,
		thousandsSep,
		decimalPointSep,
		maxLeftLengthWithSep,
	} = useMemo(() => {
		const { decimalDigits, thousandsSeparator, decimalSeparator } =
			getCcyDataByCcy(ccy);
		return {
			inputRegEx: getInputRegEx(decimalDigits, decimalSeparator),
			numDecimals: decimalDigits,
			placeholder:
				decimalDigits > 0
					? `0${decimalSeparator}`.padEnd(decimalDigits + 2, "0")
					: "0",
			thousandsSep: thousandsSeparator,
			decimalPointSep: decimalSeparator,
			maxLeftLengthWithSep:
				MAX_NUM_CHAR_LENGTH - (decimalDigits > 0 ? decimalDigits + 1 : 0),
		};
	}, [ccy]);

	useEffect(() => {
		if (onPlaceHolderChange) {
			onPlaceHolderChange(placeholder);
		}
	}, [onPlaceHolderChange, placeholder]);

	const onChange = (val: string) => {
		if (val) {
			if (thousandsSep) {
				val = removeOnlySepNotDecimalPoint(val, thousandsSep);
			}
			if (inputRegEx.test(val)) {
				if (thousandsSep) {
					val = addThousandSeparator(val, thousandsSep, decimalPointSep);
				}
				const [left] = val.split(decimalPointSep);
				if (
					left.length <= maxLeftLengthWithSep ||
					val.length < prevVal.current.length
				) {
					prevVal.current = val;
					onUpdate(prevVal.current);
				}
			}
		} else {
			prevVal.current = "";
			onUpdate(prevVal.current);
		}
	};
	const onBlur = () => {
		setFocused(false);
		if (value) {
			let formatValue = correctDecimals(value, numDecimals, decimalPointSep);
			if (formatValue) {
				onUpdate(formatValue);
			}
		}
	};
	const onFocus = () => {
		setFocused(true);
		// this is needed because value could be updated from prop
		prevVal.current = value;
	};

	return { onChange, onBlur, onFocus, isFocused, placeholder };
};

// NOTE: assumption no country has 1 decimal digits
const getInputRegEx = (digits: number, decimalPoint: string = ".") => {
	if (digits > 0) {
		return new RegExp(
			"^((\\d+)?(\\" + decimalPoint + "\\d{0," + digits + "})?)$"
		); //    /^((\d+)?(\.\d{0,2})?)$/
	}
	return /^\d+$/;
};
